import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Columns, Layout, Grid, Rows, Square, RotateCcw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "The Flexible Box",
        icon: "🧘",
        content: (
            <div className="space-y-6">
                <div className="bg-pink-50 p-6 rounded-2xl border-2 border-pink-200">
                    <h3 className="font-bold text-xl text-pink-800 mb-3">One Direction</h3>
                    <p className="text-slate-700 mb-4">
                        <strong>Flexbox</strong> is a layout mode that arranges items in a <em>single direction</em>: either a Row or a Column.
                    </p>
                    <p className="text-slate-700">
                        Think of a string of beads. You can slide them around, space them evenly, or squish them together!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Main Axis vs Cross Axis",
        icon: "📐",
        content: (
            <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <h3 className="font-bold text-blue-800 mb-2">justify-content</h3>
                        <p className="text-slate-600 text-sm">Aligns items along the <strong>Main Axis</strong> (usually horizontal).</p>
                        <div className="mt-2 bg-white h-8 w-full flex justify-end px-1 border border-blue-200 rounded text-xs gap-1">
                            <div className="bg-blue-400 w-4 h-full"></div>
                            <div className="bg-blue-400 w-4 h-full"></div>
                        </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <h3 className="font-bold text-green-800 mb-2">align-items</h3>
                        <p className="text-slate-600 text-sm">Aligns items along the <strong>Cross Axis</strong> (usually vertical).</p>
                        <div className="mt-2 bg-white h-12 w-full flex items-center justify-center border border-green-200 rounded text-xs">
                            <div className="bg-green-400 w-12 h-4"></div>
                        </div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🦍",
        content: (
            <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200">
                    <h3 className="font-bold text-xl text-purple-800 mb-3">Gorilla Yoga</h3>
                    <p className="text-slate-700 mb-4">
                        Our Gorillas need to stretch! Use Flexbox properties to solve the layout challenges.
                    </p>
                    <p className="font-bold text-purple-900">
                        Complete at least 2 challenges to pass!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC & COMPONENTS ============
const GORILLAS = [
    { id: 1, name: 'Scout', emoji: '🎒' },
    { id: 2, name: 'Chef', emoji: '👨‍🍳' },
    { id: 3, name: 'Artist', emoji: '🎨' },
    { id: 4, name: 'Scientist', emoji: '🔬' },
    { id: 5, name: 'Explorer', emoji: '🧭' },
];

type JustifyContent = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
type AlignItems = 'flex-start' | 'flex-end' | 'center' | 'stretch';
type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';

interface Preset {
    name: string;
    icon: React.ReactNode;
    settings: {
        justifyContent: JustifyContent;
        alignItems: AlignItems;
        flexDirection: FlexDirection;
        gap: number;
        flexWrap: boolean;
    };
}

const PRESETS: Preset[] = [
    {
        name: 'Navbar',
        icon: <Columns size={16} />,
        settings: { justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row', gap: 16, flexWrap: false }
    },
    {
        name: 'Card Grid',
        icon: <Grid size={16} />,
        settings: { justifyContent: 'flex-start', alignItems: 'flex-start', flexDirection: 'row', gap: 24, flexWrap: true }
    },
    {
        name: 'Centered',
        icon: <Square size={16} />,
        settings: { justifyContent: 'center', alignItems: 'center', flexDirection: 'row', gap: 16, flexWrap: false }
    },
    {
        name: 'Sidebar',
        icon: <Layout size={16} />,
        settings: { justifyContent: 'flex-start', alignItems: 'stretch', flexDirection: 'column', gap: 8, flexWrap: false }
    },
    {
        name: 'Footer',
        icon: <Rows size={16} />,
        settings: { justifyContent: 'space-evenly', alignItems: 'center', flexDirection: 'row', gap: 32, flexWrap: true }
    },
];

const CHALLENGES = [
    {
        id: 1,
        title: 'Center Everything',
        description: 'Put all items in the center of the container',
        check: (j: JustifyContent, a: AlignItems) => j === 'center' && a === 'center'
    },
    {
        id: 2,
        title: 'Space It Out',
        description: 'Use space-between to spread items',
        check: (j: JustifyContent) => j === 'space-between'
    },
    {
        id: 3,
        title: 'Column Stack',
        description: 'Stack items vertically',
        check: (_j: JustifyContent, _a: AlignItems, d: FlexDirection) => d === 'column'
    },
];

const FlexGame = ({ onComplete }: { onComplete: () => void }) => {
    const [challengesSolved, setChallengesSolved] = useState<number[]>([]);
    const [justifyContent, setJustifyContent] = useState<JustifyContent>('flex-start');
    const [alignItems, setAlignItems] = useState<AlignItems>('center');
    const [flexDirection, setFlexDirection] = useState<FlexDirection>('row');
    const [gap, setGap] = useState(16);
    const [flexWrap, setFlexWrap] = useState(false);
    const [itemCount, setItemCount] = useState(4);

    const handleExperiment = () => {
        // Check challenges
        const newSolved = [...challengesSolved];
        CHALLENGES.forEach(ch => {
            if (ch.check(justifyContent, alignItems, flexDirection) && !newSolved.includes(ch.id)) {
                newSolved.push(ch.id);
            }
        });

        if (newSolved.length > challengesSolved.length) {
            setChallengesSolved(newSolved);
        }

        if (newSolved.length >= 2) {
            onComplete();
        }
    };

    const applyPreset = (preset: Preset) => {
        setJustifyContent(preset.settings.justifyContent);
        setAlignItems(preset.settings.alignItems);
        setFlexDirection(preset.settings.flexDirection);
        setGap(preset.settings.gap);
        setFlexWrap(preset.settings.flexWrap);

        // Timeout to allow state update before checking
        setTimeout(() => {
            // Re-run checking logic manually or rely on effect. 
            // Simpler here: check immediately with new values
            const newSolved = [...challengesSolved];
            CHALLENGES.forEach(ch => {
                if (ch.check(preset.settings.justifyContent, preset.settings.alignItems, preset.settings.flexDirection) && !newSolved.includes(ch.id)) {
                    newSolved.push(ch.id);
                }
            });
            if (newSolved.length > challengesSolved.length) {
                setChallengesSolved(newSolved);
            }
            if (newSolved.length >= 2) {
                onComplete();
            }
        }, 100);
    };

    const justifyOptions: JustifyContent[] = ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'];
    const alignOptions: AlignItems[] = ['flex-start', 'flex-end', 'center', 'stretch'];
    const directionOptions: FlexDirection[] = ['row', 'row-reverse', 'column', 'column-reverse'];

    const displayedGorillas = GORILLAS.slice(0, itemCount);

    return (
        <div>
            {/* Presets & Challenges */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex gap-2 items-center">
                    <span className="text-sm font-bold text-slate-500">Presets:</span>
                    {PRESETS.map(preset => (
                        <button
                            key={preset.name}
                            onClick={() => applyPreset(preset)}
                            className="px-4 py-2 bg-white border-2 border-slate-200 hover:border-pink-400 rounded-xl text-sm font-bold text-slate-700 flex items-center gap-2 transition-all"
                        >
                            {preset.icon}
                            {preset.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Challenges */}
            <div className="flex justify-center gap-3 mb-8">
                {CHALLENGES.map(ch => {
                    const solved = challengesSolved.includes(ch.id);
                    return (
                        <div
                            key={ch.id}
                            className={`px-4 py-2 rounded-xl text-sm font-bold border-2 ${solved
                                ? 'bg-green-100 border-green-400 text-green-700'
                                : 'bg-slate-100 border-slate-200 text-slate-500'
                                }`}
                        >
                            {solved ? '✅' : '⬜'} {ch.title}
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Controls Panel */}
                <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-slate-100 space-y-6">
                    <h2 className="font-bold text-lg text-slate-700 flex items-center gap-2">
                        <span className="w-8 h-8 bg-pink-100 text-pink-600 rounded-lg flex items-center justify-center text-sm">⚙️</span>
                        Controls
                    </h2>

                    {/* Item Count */}
                    <div>
                        <label className="text-sm font-bold text-slate-600 mb-2 block">Items: {itemCount}</label>
                        <input
                            type="range" min="1" max="5" value={itemCount}
                            onChange={e => setItemCount(Number(e.target.value))}
                            className="w-full accent-pink-500"
                        />
                    </div>

                    {/* Flex Direction */}
                    <div>
                        <label className="text-sm font-bold text-slate-600 mb-2 block">flex-direction</label>
                        <div className="grid grid-cols-2 gap-2">
                            {directionOptions.map(opt => (
                                <button
                                    key={opt}
                                    onClick={() => { setFlexDirection(opt); handleExperiment(); }}
                                    className={`px-3 py-2 rounded-lg text-xs font-bold border-2 transition-all ${flexDirection === opt ? 'bg-pink-500 text-white border-pink-500' : 'bg-white border-slate-200 text-slate-600 hover:border-pink-300'}`}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Justify Content */}
                    <div>
                        <label className="text-sm font-bold text-slate-600 mb-2 block">justify-content</label>
                        <div className="flex flex-wrap gap-2">
                            {justifyOptions.map(opt => (
                                <button
                                    key={opt}
                                    onClick={() => { setJustifyContent(opt); handleExperiment(); }}
                                    className={`px-2 py-1 rounded-lg text-xs font-bold border-2 transition-all ${justifyContent === opt ? 'bg-blue-500 text-white border-blue-500' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'}`}
                                >
                                    {opt.replace('flex-', '').replace('space-', 's-')}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Align Items */}
                    <div>
                        <label className="text-sm font-bold text-slate-600 mb-2 block">align-items</label>
                        <div className="flex flex-wrap gap-2">
                            {alignOptions.map(opt => (
                                <button
                                    key={opt}
                                    onClick={() => { setAlignItems(opt); handleExperiment(); }}
                                    className={`px-2 py-1 rounded-lg text-xs font-bold border-2 transition-all ${alignItems === opt ? 'bg-green-500 text-white border-green-500' : 'bg-white border-slate-200 text-slate-600 hover:border-green-300'}`}
                                >
                                    {opt.replace('flex-', '')}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Gap */}
                    <div>
                        <label className="text-sm font-bold text-slate-600 mb-2 block">gap: {gap}px</label>
                        <input
                            type="range" min="0" max="64" value={gap}
                            onChange={e => { setGap(Number(e.target.value)); handleExperiment(); }}
                            className="w-full accent-purple-500"
                        />
                    </div>

                    {/* Flex Wrap */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            checked={flexWrap}
                            onChange={e => { setFlexWrap(e.target.checked); handleExperiment(); }}
                            className="w-5 h-5 accent-purple-500"
                        />
                        <label className="text-sm font-bold text-slate-600">flex-wrap: wrap</label>
                    </div>

                    <button
                        onClick={() => { setJustifyContent('flex-start'); setAlignItems('center'); setFlexDirection('row'); setGap(16); setFlexWrap(false); setItemCount(4); }}
                        className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                        <RotateCcw size={16} /> Reset
                    </button>

                    <button
                        onClick={handleExperiment}
                        className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white font-bold rounded-xl transition-colors shadow-lg shadow-pink-200"
                    >
                        Check Code
                    </button>
                </div>

                {/* Preview Area */}
                <div className="lg:col-span-2 bg-white rounded-3xl shadow-lg border-2 border-slate-100 overflow-hidden">
                    <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-500">Preview Container</span>
                        <span className="text-xs font-mono text-slate-400 bg-white px-2 py-1 rounded border border-slate-200">
                            display: flex
                        </span>
                    </div>

                    <motion.div
                        layout
                        className="min-h-[400px] p-6 border-4 border-dashed border-pink-200 m-6 rounded-2xl bg-pink-50/50"
                        style={{
                            display: 'flex',
                            justifyContent,
                            alignItems,
                            flexDirection,
                            gap: `${gap}px`,
                            flexWrap: flexWrap ? 'wrap' : 'nowrap',
                        }}
                    >
                        {displayedGorillas.map((g, i) => (
                            <motion.div
                                key={g.id}
                                layout
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: i * 0.1 }}
                                className="bg-white rounded-2xl p-4 shadow-md border-2 border-slate-100 flex flex-col items-center min-w-[100px]"
                                style={{ minHeight: alignItems === 'stretch' ? '100%' : 'auto' }}
                            >
                                <div className="text-4xl">{g.emoji}</div>
                                <span className="text-xs font-bold text-slate-600 mt-2">{g.name}</span>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Generated CSS */}
                    <div className="bg-slate-900 text-green-400 p-4 m-6 mt-0 rounded-xl font-mono text-sm">
                        <div className="text-slate-500 mb-2">/* Generated CSS */</div>
                        <div>.container {'{'}</div>
                        <div className="pl-4">display: flex;</div>
                        <div className="pl-4">flex-direction: <span className="text-yellow-400">{flexDirection}</span>;</div>
                        <div className="pl-4">justify-content: <span className="text-blue-400">{justifyContent}</span>;</div>
                        <div className="pl-4">align-items: <span className="text-pink-400">{alignItems}</span>;</div>
                        <div className="pl-4">gap: <span className="text-purple-400">{gap}px</span>;</div>
                        {flexWrap && <div className="pl-4">flex-wrap: <span className="text-orange-400">wrap</span>;</div>}
                        <div>{'}'}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const FlexboxPlayground = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('flexbox', 100);
    };

    return (
        <LessonWrapper
            moduleName="Flexbox Yoga"
            moduleEmoji="🧘"
            moduleColor="pink"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Playground"
        >
            <FlexGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default FlexboxPlayground;
